import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Check, MapPin, Truck, Container, Recycle, Hammer, FileText } from "lucide-react";
import { CONTACT, CITY_COORDS, IMAGES } from "@/lib/data";
import { CITY_CONTENT as EN_CITY_CONTENT } from "@/lib/cityContent";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";
import { SectionHeading, BottomWave, reveal } from "@/components/site/primitives";
import LeadForm from "@/components/site/LeadForm";
import Seo from "@/components/site/Seo";
import { CTABand } from "@/pages/BusinessWaste";

export const citySlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function nearbyCities(city, count = 6) {
  return CITY_COORDS
    .filter((c) => c.name !== city.name)
    .map((c) => ({ ...c, d: Math.hypot(c.lat - city.lat, c.lng - city.lng) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, count);
}

const SERVICE_ICONS = [Container, Truck, Recycle];
const EN_SERVICES = [
  { title: "Front-Load Dumpsters", desc: "1–8 yard commercial bins on weekly schedules for restaurants, offices, retail and multi-family properties." },
  { title: "Roll-Off Dumpsters", desc: "10–40 yard open-top containers for construction, demolition, roofing and large cleanout projects." },
  { title: "Recycling & Compliance", desc: "Cardboard, single-stream and organics recycling to cut disposal costs and stay compliant." },
];

export default function CityLanding() {
  const { slug } = useParams();
  const { t, lang, localePath } = useI18n();
  const content = useContent();
  const city = CITY_COORDS.find((c) => citySlug(c.name) === slug);

  if (!city) {
    return (
      <section className="min-h-[60vh] grid place-items-center pt-32 pb-20 px-5 text-center" data-testid="city-not-found">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-[#0B0F14]">{t("city.notFoundTitle")}</h1>
          <p className="mt-3 text-slate-600">{t("city.notFoundPre")} <Link to={localePath("/dumpster-rentals")} className="text-emerald-700 font-semibold underline">{t("city.notFoundLink")}</Link>.</p>
        </div>
      </section>
    );
  }

  // Interpolation helper for city/phone placeholders.
  const fill = (key) => t(key).replaceAll("{city}", city.name).replaceAll("{phone}", CONTACT.phone);

  const near = nearbyCities(city);
  // Merge English base (keeps neighborhoods proper nouns) with Spanish overlay when active.
  const enRich = EN_CITY_CONTENT[slug];
  const rich = enRich
    ? { ...enRich, ...(lang === "es" ? (content.CITY_CONTENT?.[slug] || {}) : {}) }
    : null;

  const services = lang === "es" && content.CITY_SERVICES ? content.CITY_SERVICES : EN_SERVICES;
  const ROLLOFF_SIZES = content.ROLLOFF_SIZES;
  const DUMPSTER_SIZES = content.DUMPSTER_SIZES;

  const title = fill("city.seoTitle");
  const description = fill("city.seoDesc");
  const path = `/dumpster-rental/${slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Dumpster Rental & Commercial Waste Management",
    provider: {
      "@type": "LocalBusiness",
      name: CONTACT.company,
      telephone: CONTACT.phonePlain,
      areaServed: `${city.name}, Florida`,
      email: CONTACT.email,
    },
    areaServed: { "@type": "City", name: `${city.name}, FL` },
    name: `Dumpster Rental in ${city.name}, FL`,
    description,
    url: `https://eewaste.com${path}`,
  };

  const baseFaqs = [
    { q: fill("city.baseFaq0q"), a: fill("city.baseFaq0a") },
    { q: fill("city.baseFaq1q"), a: fill("city.baseFaq1a") },
    { q: fill("city.baseFaq2q"), a: fill("city.baseFaq2a") },
  ];

  const faqs = rich ? [...rich.faqs, ...baseFaqs] : baseFaqs;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: "https://eewaste.com/" },
      { "@type": "ListItem", position: 2, name: t("sa.crumb"), item: "https://eewaste.com/service-areas" },
      { "@type": "ListItem", position: 3, name: city.name, item: `https://eewaste.com${path}` },
    ],
  };

  const whyFeatures = (t("city.whyFeatures") || []).map((f) => f.replaceAll("{city}", city.name));

  return (
    <>
      <Seo title={title} description={description} path={path} jsonLd={[jsonLd, faqJsonLd, breadcrumbJsonLd]} image={IMAGES.rolloffDumpster} noindex={!rich} />

      {/* HERO */}
      <section className="relative bg-[#0E3B3B] text-white pt-32 pb-28 overflow-hidden" data-testid="city-hero">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 85% 15%, rgba(16,185,129,0.55), transparent 45%)" }} />
        <div className="relative z-[2] max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 mb-4">
              <MapPin size={14} /> {city.name}{t("city.county")}
            </div>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl">
              {t("city.h1Pre")} <span className="text-emerald-400">{city.name}, FL</span>
            </h1>
            <p className="mt-5 text-white/80 leading-relaxed max-w-xl">
              {rich ? rich.intro : fill("city.introFallback")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/25 text-white font-semibold hover:border-emerald-400 hover:text-emerald-300 transition-all" data-testid="city-call-btn">
                <Phone size={18} /> {CONTACT.phone}
              </a>
              <Link to={localePath("/quick-quote")} className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 active:scale-95 transition-all" data-testid="city-quote-btn">
                {fill("city.quoteBtn")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <div className="bg-[#F0EFEA] rounded-3xl p-6 shadow-2xl max-w-md mx-auto">
              <img loading="lazy" decoding="async" src={IMAGES.rolloffDumpster} alt={`Roll-off dumpster rental in ${city.name}, FL`} className="w-full rounded-xl" />
            </div>
          </motion.div>
        </div>
        <BottomWave />
      </section>

      {/* SERVICES */}
      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={`${t("city.servicesEyebrowPre")} ${city.name}`} title={t("city.servicesTitle")} highlight={city.name} className="mb-14" />
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <motion.div key={s.title} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className="bg-white border border-[#E7E5DF] rounded-3xl p-8 shadow-soft" data-testid={`city-service-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-emerald-600/10 text-emerald-700 mb-5"><Icon size={24} /></span>
                  <h3 className="font-display font-bold text-lg text-[#0B0F14]">{s.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOCAL KNOWLEDGE — rich cities only */}
      {rich && (
        <section className="relative bg-[#0E3B3B] text-white py-24" data-testid="city-local">
          <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(16,185,129,0.5), transparent 45%)" }} />
          <div className="relative z-[2] max-w-[1400px] mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-[1fr_1fr] gap-12 items-start">
              <div>
                <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold">{t("city.localEyebrow")}</span>
                <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                  {t("city.localTitlePre")} <span className="text-emerald-400">{city.name}</span>
                </h2>
                <p className="mt-6 text-white/75 leading-relaxed">{fill("city.hoodsIntro")}</p>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {rich.neighborhoods.map((n) => (
                    <span key={n} className="px-4 py-2 rounded-full bg-white/[0.08] border border-white/15 text-sm text-white/90" data-testid={`city-hood-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{n}</span>
                  ))}
                </div>
                <div className="mt-8 flex items-start gap-3 bg-white/[0.06] border border-white/12 rounded-2xl p-5" data-testid="city-permit">
                  <FileText size={20} className="text-emerald-300 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white">{t("city.permitTitlePre")} {city.name}</div>
                    <p className="mt-1 text-white/70 text-sm leading-relaxed">{rich.permitNote}</p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-display font-bold text-xl mb-5">{t("city.projectsTitlePre")} {city.name}</h3>
                <div className="space-y-3">
                  {rich.projects.map((p, i) => (
                    <div key={p.title} className="flex items-start gap-4 bg-white/[0.06] border border-white/12 rounded-2xl p-5" data-testid={`city-project-${i}`}>
                      <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-300 shrink-0"><Hammer size={18} /></span>
                      <div>
                        <div className="font-semibold text-white">{p.title}</div>
                        <p className="mt-1 text-white/70 text-sm leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-white/60 text-sm flex items-start gap-2"><Truck size={16} className="text-emerald-300 shrink-0 mt-0.5" />{rich.deliveryNote}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SIZES */}
      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("city.sizesEyebrow")} title={`${t("city.sizesTitlePre")} ${city.name}`} className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROLLOFF_SIZES.map((d, i) => (
              <Link key={d.size} to={localePath(`/dumpster-rentals/${d.slug}`)}
                className="bg-white border border-[#E7E5DF] rounded-3xl p-7 shadow-soft flex flex-col hover:-translate-y-1 hover:border-emerald-600/40 transition-all" data-testid={`city-rolloff-${i}`}>
                <div className="mb-4 grid place-items-center h-40"><img loading="lazy" decoding="async" src={d.img} alt={`${d.size} roll-off dumpster in ${city.name}`} className="max-h-40 w-auto object-contain mix-blend-multiply" /></div>
                <h3 className="font-display font-bold text-xl text-[#0B0F14]">{d.size}</h3>
                <p className="mt-2 text-slate-600 text-sm flex-1">{d.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-semibold text-emerald-700 text-sm">{t("city.viewDetails")} <ArrowRight size={15} /></span>
              </Link>
            ))}
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-3 max-w-4xl mx-auto">
            {DUMPSTER_SIZES.map((d) => (
              <Link key={d.size} to={localePath("/business-waste-service")} className="flex items-center gap-2.5 text-sm text-[#0B0F14] hover:text-emerald-700 transition-colors">
                <span className="grid place-items-center w-5 h-5 rounded-full bg-emerald-600/10 text-emerald-700"><Check size={13} strokeWidth={3} /></span>
                {t("city.frontloadLabel").replaceAll("{size}", d.size).replaceAll("{city}", city.name)}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY + FORM */}
      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1fr] gap-12 items-start">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionHeading eyebrow={t("city.whyEyebrow")} title={fill("city.whyTitle")} highlight={t("city.whyHighlight")} />
            <p className="mt-6 text-slate-600 leading-relaxed">{fill("city.whyBody")}</p>
            <ul className="mt-6 space-y-3">
              {whyFeatures.map((f) => (
                <li key={f} className="flex items-center gap-3 text-[#0B0F14]">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-emerald-600/10 text-emerald-700"><Check size={14} strokeWidth={3} /></span>{f}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <LeadForm heading={fill("city.formHeading")} />
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow="FAQ" title={fill("city.faqTitle")} highlight={t("city.faqHighlight")} className="mb-12" />
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white border border-[#E7E5DF] rounded-2xl p-6 shadow-soft" data-testid={`city-faq-${i}`}>
                <h3 className="font-display font-bold text-[#0B0F14]">{f.q}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEARBY internal links */}
      <section className="relative bg-[#FBFAF8] py-16 border-t border-[#E7E5DF]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">{t("city.nearby")}</p>
          <div className="flex flex-wrap gap-2.5">
            {near.map((n) => (
              <Link key={n.name} to={localePath(`/dumpster-rental/${citySlug(n.name)}`)}
                className="px-4 py-2 rounded-full bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] hover:border-emerald-600/50 hover:text-emerald-700 transition-colors" data-testid={`nearby-${citySlug(n.name)}`}>
                {t("city.nearbyLinkPre")} {n.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
